import AccountAddress from "@/components/templates/Account/account-templates/account-address";
import AccountBusiness from "@/components/templates/Account/account-templates/account-business";
import AccountInfoEdit from "@/components/templates/Account/account-templates/account-info-edit";
import AccountInformation from "@/components/templates/Account/account-templates/account-information";
import AccountMyOrders from "@/components/templates/Account/account-templates/account-my-orders";
import AccountOrderDetails from "@/components/templates/Account/account-templates/account-order-details";
import AccountWishlist from "@/components/templates/Account/account-templates/account-wishlist";
import { Locales } from "@/lib/i18n";
import { actions, segments } from "@/locales/segment";

/**
 * Retrieves the action key corresponding to a given slug for a specific locale.
 *
 * @param locale - The locale to use for looking up actions.
 * @param slug - The slug to match against the locale's actions.
 * @returns The action key if found; otherwise, `null`.
 */
export const getActionFromSlug = (locale: Locales, slug: string) => {
  const localeActions = actions[locale as keyof typeof actions];
  if (!localeActions) return null;
  return Object.keys(localeActions).find((key) => localeActions[key] === slug);
};

/**
 * Determines if the provided slug matches the "account" segment for the given locale.
 *
 * @param locale - The locale to use for segment lookup.
 * @param slug - The slug to compare against the locale's account segment.
 * @returns `true` if the slug matches the account segment for the locale, otherwise `false`.
 */
export const isAccountSegment = (locale: Locales, slug: string) => {
  const localeSegments = segments[locale as keyof typeof segments];
  if (!localeSegments) return false;
  return localeSegments.account === slug;
};

export const renderAccountView = (resolution: Resolution) => {
  const slugs = resolution.slugs;
  const currentSlug = slugs[0];

  // Check if it's the main account page
  if (isAccountSegment(resolution.locale, currentSlug)) {
    return <AccountInformation resolution={resolution} />;
  }

  // Check if it's an account action
  const action = getActionFromSlug(resolution.locale, currentSlug);

  if (!action) {
    return null; // Invalid slug, will trigger 404
  }

  switch (action) {
    case "edit":
      return <AccountInfoEdit resolution={resolution} />;
    case "address":
      return <AccountAddress resolution={resolution} />;
    case "orders":
      // Check if there's a second slug (order ID)
      if (slugs?.length > 1 && slugs[1]) {
        return <AccountOrderDetails locale={resolution?.locale} orderId={slugs[1]} />;
      }
      return <AccountMyOrders resolution={resolution} />;
    case "wishlist":
      return <AccountWishlist resolution={resolution} />;
    case "business":
      return <AccountBusiness resolution={resolution} />;
    default:
      return null;
  }
};
